import { AI_FACT_NO_WRITE_KEY, AI_FACT_WARNING_KEY, type AiCapabilityPage, type AiSlot, type AiSlotFileImportKind, type AiSlotType } from "../registry/schema.js";
import { AI_MENU_CATALOG_ENTRY_COUNT, type AiMenuSection } from "../registry/menu-catalog.js";
import type { AiFallbackReason } from "../interpreter/confidence-policy.js";
import type { LlmBlockReason } from "../llm-block-reason.js";
import { SLOT_RESOLUTION_REASON } from "../resolvers/slot-resolution.js";
import type { AiTaskState } from "../task/task-state.js";
import { listPlaceholders, renderTemplate, resolveOptionalSegments, AiTemplateRenderError, type TemplateValues } from "./render-template.js";

/**
 * 🔴 US-AIV2-0023-S01 (@UC0163 @UC0169) — LƯỢT trợ lý (hình dây 0024) render từ template Sổ + ô ĐÃ RESOLVE + facts server.
 *
 * Hình dây do mobile khai trước (`apps/mobile/.../ai_turn.dart`, fixture 8 mẫu); contracts `ai/turn.ts` là zod của cùng hình và có test
 * parity với đầu ra ở đây. Domain KHÔNG import contracts (chiều contracts → domain), nên kiểu ở đây là nguồn, contracts khoá theo.
 *
 * Luật:
 *  - TIER quyết độ nặng xác nhận (@UC0169): `T1` một chạm (câu + chip) · `T2` phiếu đầy đủ (card một dòng/chỗ trống của `templates.confirm`,
 *    nhãn từ `page.labels` — thiếu ⇒ ném, P5.3) · `read` không phiếu (`answer` từ `reply`) · `redirect` chỉ nút mở màn hình (AC3: KHÔNG confirm).
 *  - Mọi giá trị chèn là CHUỖI server đã format (`@UC0151`); chip payload chỉ chuỗi; tập kind ĐÓNG; số chip ≤ trần của ĐÚNG
 *    loại lượt: ô chọn `AI_TURN_MAX_CHIPS`, menu `AI_MENU_MAX_CHIPS` (US-AIV2-0076 — hai trần, hai lý do). Menu KHÁM PHÁ (lượt chào ·
 *    «chưa rõ ý» · «làm gì tiếp») ghép ở `discovery-menu.ts` theo catalog (AI-MENU-A); `renderMenu` ở đây chỉ còn cho lượt CHỌN giữa vài trang.
 *  - Chip `confirm` mang `taskId` + `version` (0021 `expectedVersion`: thứ chạy = thứ người đã thấy); mọi chip `cancel`/`choose` mang `taskId`.
 *  - Route `open_screen` phải ĐÃ THẾ tham số: còn `:` hay tham số có `/`·`:` ⇒ ném (mobile từ chối route rác — #810 M1).
 *  - Client không tự nghĩ ra nút: không chip trong lượt ⇒ không nút. `greeting` là của Người dẫn đường (0061) — không render ở đây.
 */
export const AI_TURN_TYPES = ["greeting", "answer", "ask_slot", "clarify", "confirm", "menu", "redirect"] as const;
export type AiTurnType = (typeof AI_TURN_TYPES)[number];

/** `open_screen` là kind DUY NHẤT client tự thực thi; mọi kind khác gửi nguyên `{kind,payload}` lên server. */
export const AI_CHIP_KINDS = ["run_page", "choose", "confirm", "cancel", "open_screen"] as const;
export type AiChipKind = (typeof AI_CHIP_KINDS)[number];

/**
 * Trần chip của **Ô CHỌN** — `ask_slot` (danh sách resolver liệt kê sẵn) và `clarify` (ứng viên khi hỏi lại). Quá trần thì cắt
 * ứng viên, **không bao giờ** cắt `cancel`.
 *
 * 🔴 **LÝ DO RIÊNG, và là lý do DUY NHẤT của con số này**: 0031 QĐ-6 nâng 8 → 10 để ô enum 9 giá trị (loại nhóm = 9 code
 * `group_categories` bật) liệt kê **ĐỦ** + nút Huỷ ⇒ **10 = 9 + 1**. Mobile `Wrap` xuống dòng, **không cắt** ở đây.
 *
 * 🔴 Nó **chưa bao giờ** là một giới hạn đọc, nên **đừng dùng nó cho menu khám phá** — đó là `AI_MENU_MAX_CHIPS`
 * (US-AIV2-0076). Trước khi tách, chính con số "9 enum + Huỷ" này quyết định ai rơi khỏi menu: đo 19/09, owner mất chip của
 * `READ-PEOPLE-MINE` · `HELP-FROM-REGISTRY` · `GROUP-BANK-ACCOUNT` — ba việc trợ lý LÀM ĐƯỢC — vì chúng đứng thứ 11-13 trong
 * thứ tự khai Sổ, mà `registry.ts` đã ghi thẳng rằng thứ tự ấy *"không phải ưu tiên"*.
 */
export const AI_TURN_MAX_CHIPS = 10;

/**
 * 🔴🔴 Trần chip của **MENU KHÁM PHÁ** — lượt chào · «chưa rõ ý» · «làm gì tiếp» (`discovery-menu.ts`) và lượt chọn vài trang (`renderMenu`).
 *
 * AI-MENU-A (@UC0353 · GOPPY-45): menu nay ghép theo CATALOG (`registry/menu-catalog.ts`) — mỗi mục catalog (trang hoặc họ gộp) TỐI ĐA một
 * nút, trước chúng là các nút DẪN (nút thẻ của Người dẫn đường + trang caller ghim). Nên trần **dẫn xuất** từ đúng hai thứ đó, không ai gõ:
 * thêm một mục catalog ⇒ trần tự lớn trong cùng commit; trần dây (`contracts` `AI_TURN_CHIPS_MAX = max(…)`) tự theo ⇒ không có menu hợp lệ
 * nào bị chính hợp đồng của mình trả 400 ở biên.
 *
 * Thay cho trần cũ «số trang chat của Sổ + 1 chỗ cho thẻ trỏ `redirect`» (US-AIV2-0076 · QĐ PO 25/09): trần đó đếm trang chat vì menu
 * cũ là MỌI trang theo thứ tự Sổ, và một nút thẻ trỏ trang `redirect` ăn mất suất của trang chat cuối danh sách. Nay nút dẫn có chỗ RIÊNG
 * (`AI_MENU_LEAD_RESERVE`) ⇒ không nút dẫn nào đẩy được một mục catalog ra ngoài — đúng điều kiện «XEM LẠI KHI 0076 chọn hướng 2/3» mà
 * docblock cũ đã hẹn (chỗ-dành-riêng redirect về 0, ngân sách thẻ-trỏ-redirect thôi là một ràng buộc của menu).
 *
 * Bất biến thật nằm ở `menu-chip-cap.invariants.spec.ts`: mọi trang chat được phép (không ẩn) TỚI ĐƯỢC từ lượt — trực tiếp hoặc qua nút họ.
 */
/** Số nút THẺ tối đa trong một lượt — nguồn của `GUIDE_MAX_CARD_BUTTONS` (= số nút app hiện sẵn, parity ở api `menu-fold.invariants`). */
export const AI_MENU_CARD_SLOTS = 3;
/**
 * Số trang caller được GHIM ngay sau nút thẻ (đoán việc gần nhất của lượt «chưa rõ ý» · lời mời đang chờ của người chưa có nhóm · đường
 * sửa khi lời báo đã vào sổ). = số nút hiện sẵn: ghim là để LẤP đúng các chỗ đó, không hơn.
 */
export const AI_MENU_PINNED_MAX = 3;
/** Chỗ cho nút DẪN (thẻ + ghim) đứng trước phần catalog — chúng có thể trỏ trang NGOÀI catalog (redirect), nên phải có chỗ riêng. */
export const AI_MENU_LEAD_RESERVE = AI_MENU_CARD_SLOTS + AI_MENU_PINNED_MAX;

export const AI_MENU_MAX_CHIPS = AI_MENU_CATALOG_ENTRY_COUNT + AI_MENU_LEAD_RESERVE;
/** Nhãn nút huỷ — chuỗi dữ liệu server, không qua ARB (0024). */
export const AI_CHIP_CANCEL_LABEL = "Huỷ";
export const AI_CHIP_CONFIRM_LABEL = "Đồng ý";
/** Câu mặc định khi trang T2 không khai `templates.clarify`. */
export const AI_CLARIFY_DEFAULT_TEXT = "Mình chưa chắc bạn nói đến cái nào — bạn chọn giúp nhé.";
export const AI_MENU_TEXT = "Mình chưa rõ ý bạn. Bạn muốn làm gì?";
/**
 * 🔴 GUIDE-FIX-20261020 S3 (@UC0368) — câu menu TRUNG TÍNH cho lượt không đáp một câu gõ nào (làm mới dòng dẫn đường khi quay lại app / về từ
 * màn `open_screen`, mà không còn dòng dẫn đường nào để nói). `AI_MENU_TEXT` ("chưa rõ ý") chỉ dành cho câu GÕ mà trợ lý chưa hiểu — người
 * dùng chưa gõ gì thì câu đó đổ lỗi oan cho họ.
 */
export const AI_MENU_NEUTRAL_TEXT = "Bạn muốn làm gì tiếp?";
/**
 * 🔴 US-AIV2-0109 S05 (@UC0241) — "chưa hiểu" khi người này CÓ việc chat bị khoá (vai không đủ). Người phiên dịch không thấy trang
 * bị khoá (danh mục đã lọc theo vai), nên câu gõ đúng việc của quản lý vẫn ra `unknown`. Nói "chưa rõ ý" trần là đổ lỗi cho người
 * dùng; câu này nói cả khả năng thật. Engine chỉ dùng câu này khi đo được có việc bị khoá — không đoán từ tên vai.
 */
export const AI_MENU_TEXT_MAYBE_MANAGER = "Mình chưa rõ ý bạn — hoặc việc đó cần người quản lý nhóm làm. Bạn chọn một việc bên dưới nhé.";

export interface AiTurnChip {
  readonly kind: AiChipKind;
  readonly label: string;
  readonly payload: Readonly<Record<string, string>>;
  /** Nút chính (nền đặc) — SERVER quyết, client không suy từ `kind` (review #810). */
  readonly primary?: true;
  /**
   * AI-MENU-A (@UC0353) — MỤC của nút trên màn Khám phá (khoá wire của catalog menu). Chỉ nút menu/chào mang; vắng ⇒ app xếp «Việc khác»
   * (lượt cũ đã lưu · nút thẻ trỏ trang ngoài catalog). Client không gửi lại trường này (chip gửi lên chỉ `{kind, payload}`).
   */
  readonly section?: AiMenuSection;
}
export interface AiTurnCardLine {
  readonly label: string;
  readonly value: string;
}
export interface AiTurnCard {
  readonly title?: string;
  readonly lines: readonly AiTurnCardLine[];
}
export interface AiTurn {
  readonly type: AiTurnType;
  readonly text: string;
  readonly chips: readonly AiTurnChip[];
  readonly taskId?: string;
  readonly taskState?: AiTaskState;
  readonly page?: string;
  readonly slot?: { readonly id: string; readonly type: AiSlotType; readonly fileImport?: AiSlotFileImportKind };
  readonly card?: AiTurnCard;
  /**
   * 🔴 US-AIV2-0066 (@UC0219) — MỘT dòng nhắc trên phiếu xác nhận, hiển thị RIÊNG (⚠️), không trộn
   * vào `text`. Máy cảnh báo, người quyết: trường này **không** đổi chip, không chặn `confirm`.
   */
  readonly warning?: string;
  /** 🔴 US-AIV2-0109 S01 (@UC0237) — mã lỗi ổn định của lượt THẤT BẠI, cho máy đọc; câu ở `text` không chở mã. Chỉ ở `answer`. */
  readonly failureCode?: string;
  /** GUIDE-FIX S2 (@UC0368) — TẬP lõi câu (không theo chỉ số dòng) khi có dòng mang giọng dẫn đường (`guideLineCore`); máy đọc, không hiển thị. Xem contracts `aiTurnSchema`. */
  readonly lineCores?: readonly string[];
}

/** Ảnh phiếu việc mà render cần — không phải bản ghi DB (domain không biết Prisma). */
export interface AiTaskView {
  readonly id: string;
  readonly state: AiTaskState;
  readonly version: number;
}

/** Ứng viên để hỏi lại — `value` là chuỗi gửi lại qua chip (`choice`), `label` là nhãn hiển thị. */
export interface AiChoiceView {
  readonly value: string;
  readonly label: string;
}

const cancelChip = (taskId: string): AiTurnChip => ({ kind: "cancel", label: AI_CHIP_CANCEL_LABEL, payload: { taskId } });

function templateOf(page: AiCapabilityPage, key: keyof AiCapabilityPage["templates"]): string {
  const t = page.templates[key];
  if (typeof t !== "string") throw new AiTemplateRenderError("TEMPLATE_KEY_MISSING", key, page.id);
  return t;
}

/** Nhãn chip "để trống" khi hỏi lại một ô TUỲ CHỌN — cùng chữ với lời hứa trong `slot.ask` của Sổ ("bỏ trống = …"). */
export const AI_CHIP_SKIP_LABEL = "Bỏ trống";

/**
 * Chip bỏ trống ô tuỳ chọn: `choose{choice: ""}` — server xoá ô, executor dùng mặc định trang đã hứa (review #820 H-R2: hỏi lại mà không có
 * lối "để trống" tất định thì chỉ thoát bằng Huỷ, mất phiếu). Chỉ gắn cho ô `required: false`; ô bắt buộc không có chip này.
 */
export function skipChip(taskId: string, slotId: string): AiTurnChip {
  return { kind: "choose", label: AI_CHIP_SKIP_LABEL, payload: { taskId, slot: slotId, choice: "" } };
}

/** Ô trên dây: `{id, type}` + `fileImport` khi ô nhận tệp (US-AIV2-0121 · @UC0390 — app vẽ nút đính kèm theo lời server). */
function wireSlot(slot: AiSlot): NonNullable<AiTurn["slot"]> {
  return slot.fileImport === undefined ? { id: slot.id, type: slot.type } : { id: slot.id, type: slot.type, fileImport: slot.fileImport.kind };
}

/**
 * Hỏi một ô còn thiếu — câu hỏi là `slot.ask` (có thể có chỗ trống trỏ ô/fact đã có). Ô tuỳ chọn có thêm chip "Bỏ trống".
 * `offered` (0031): resolver đã LIỆT KÊ được lựa chọn dù chưa có chữ (enum · quỹ/đợt/nhóm trong phạm vi) ⇒ chip `choose` theo ĐÚNG thứ tự
 * resolver, cắt theo trần — vẫn là `ask_slot` (hỏi ô, không phải "mơ hồ"), người dùng bấm hoặc gõ đều được.
 */
export function renderAskSlot(page: AiCapabilityPage, task: AiTaskView, slot: AiSlot, values: TemplateValues = {}, offered: readonly AiChoiceView[] = []): AiTurn {
  // "Bỏ trống" CHỈ khi ô tuỳ chọn KHÔNG có danh sách để chọn: ô quỹ/nhóm có ≥2 ứng viên thì bỏ trống = resolver liệt kê lại = hỏi lại y nguyên
  // (review #821 M6 — nút không làm gì); ô đã có danh sách thì chọn hoặc Huỷ.
  const tail = slot.required || offered.length > 0 ? [cancelChip(task.id)] : [skipChip(task.id, slot.id), cancelChip(task.id)];
  const chooses: AiTurnChip[] = offered.slice(0, AI_TURN_MAX_CHIPS - tail.length).map((c) => ({ kind: "choose", label: c.label, payload: { taskId: task.id, slot: slot.id, choice: c.value } }));
  return {
    type: "ask_slot",
    text: renderTemplate(slot.ask, values),
    taskId: task.id,
    taskState: task.state,
    page: page.id,
    slot: wireSlot(slot),
    chips: [...chooses, ...tail],
  };
}

/** Hỏi lại khi 0/≥2 khớp: một chip `choose` mỗi ứng viên theo ĐÚNG thứ tự resolver (không sắp lại), cắt theo trần, `cancel` luôn còn. */
/**
 * 🔴 US-AIV2-0110 S27b (@UC0279 · rà độc lập làn A MEDIUM-1) — danh từ của ô tên-riêng khi câu hỏi lại vì chữ KHÔNG KHỚP. Theo LOẠI ô
 * (tập đóng của Sổ), không theo id: đợt thu và "khoản đang chờ" đều là loại `collection` ⇒ "khoản" đúng cho cả hai.
 */
const REF_SLOT_NOUN: Readonly<Partial<Record<AiSlot["type"], string>>> = Object.freeze({ fund: "quỹ", collection: "khoản", group: "nhóm" });

/** Câu hỏi lại khi chữ người dùng không khớp mục nào — đúng với MỌI số lựa chọn (1 hay nhiều), không khẳng định "nhiều"/"mấy". */
export function aiClarifyUnmatchedText(noun: string): string {
  return `Mình chưa rõ bạn muốn ${noun} nào — bạn chọn giúp nhé.`;
}

/**
 * 🔴 US-AIV2-0112 lô Sổ 2 (@UC0161) — ô NGƯỜI, chữ không khớp ai (tên không có trong nhóm, "anh"/"chị", đại từ không có ghế người gõ) ⇒ resolver
 * trả CẢ danh sách. `clarify` của ô người viết cho ca NHIỀU người trùng tên — nói nó ở đây là lời khai sai. Câu này nói thật + giữ câu hỏi của ô.
 */
export function aiClarifyUnmatchedMemberText(ask: string): string {
  return `Mình chưa rõ bạn nói ai. ${ask}`;
}

/**
 * 🔴 US-AIV2-0112 H04 (@UC0162) — ô TIỀN hỏi kèm ĐÚNG MỘT nút (chỉ đuôi ghép "1tr2"/"2 triệu rưỡi" sinh ra một ứng viên tiền — `money-slot.ts`).
 * Nhãn là chuỗi `formatVndMinor` tất định của bộ đọc (không phải chữ LLM). Vẫn là câu HỎI: người dùng phải bấm hoặc gõ lại.
 */
export function aiClarifyMoneySuggestText(label: string): string {
  return `Ý bạn là ${label} phải không? Bấm để chọn, hoặc gõ lại số tiền.`;
}

export function renderClarify(
  page: AiCapabilityPage,
  task: AiTaskView,
  slot: AiSlot,
  candidates: readonly AiChoiceView[],
  values: TemplateValues = {},
  /** S27b: LÝ DO resolver ra danh sách (`SLOT_RESOLUTION_REASON`). Vắng ⇒ câu cũ. */
  reason?: string,
): AiTurn {
  // 🔴 S27b (@UC0279): ô quỹ/đợt/nhóm + chữ KHÔNG KHỚP ⇒ câu trung thực theo loại ô. `clarify` của ô và câu mặc định viết cho ca NHIỀU
  // mục khớp ("Bạn có mấy khoản…", "chưa chắc bạn nói đến cái nào") — sai với `unmatched`, nhất là khi phạm vi chỉ có 1.
  const noun = REF_SLOT_NOUN[slot.type];
  // 🔴 US-AIV2-0109 S06 (@UC0243): câu của CHÍNH Ô đang hỏi · ô enum ⇒ câu hỏi lựa chọn của nó · còn lại ⇒ câu mặc định.
  const own = slot.clarify ?? (slot.type === "enum" ? slot.ask : undefined);
  const unmatched = reason === SLOT_RESOLUTION_REASON.UNMATCHED;
  const text =
    slot.type === "money" && candidates.length === 1
      ? aiClarifyMoneySuggestText(candidates[0]!.label)
      : unmatched && noun !== undefined
        ? aiClarifyUnmatchedText(noun)
        : unmatched && slot.type === "member"
          ? aiClarifyUnmatchedMemberText(renderTemplate(slot.ask, values))
          : typeof own === "string"
            ? renderTemplate(own, values)
            : AI_CLARIFY_DEFAULT_TEXT;
  const chooses: AiTurnChip[] = candidates.slice(0, AI_TURN_MAX_CHIPS - 1).map((c) => ({
    kind: "choose",
    label: c.label,
    payload: { taskId: task.id, slot: slot.id, choice: c.value },
  }));
  return { type: "clarify", text, taskId: task.id, taskState: task.state, page: page.id, slot: wireSlot(slot), chips: [...chooses, cancelChip(task.id)] };
}

/**
 * Phiếu xác nhận — CHỈ T1/T2. T1: câu + nút; T2: thêm `card` một dòng cho MỖI chỗ trống của `templates.confirm` theo thứ tự xuất hiện,
 * nhãn từ `page.labels` (thiếu ⇒ `TEMPLATE_LABEL_MISSING`). Giá trị = đúng chuỗi đã chèn vào câu ⇒ "dòng sổ khớp phiếu từng chữ" đo được.
 */
export function renderConfirm(page: AiCapabilityPage, task: AiTaskView, resolved: TemplateValues, facts: TemplateValues): AiTurn {
  if (page.tier !== "T1" && page.tier !== "T2") throw new AiTemplateRenderError("TIER_HAS_NO_CONFIRM", page.tier, page.id);
  const values: TemplateValues = { ...resolved, ...facts };
  // 🔴 US-AIV2-0114 L05 (@UC0197): đoạn tuỳ chọn `[[…]]` của ô bỏ trống bị cắt TRƯỚC — câu và dòng phiếu đọc CÙNG template đã cắt, nên
  // ô bỏ trống không còn dòng "(không có)" và không bao giờ có dòng phiếu mà câu không nói (hay ngược lại).
  const template = resolveOptionalSegments(templateOf(page, "confirm"), values);
  const text = renderTemplate(template, values);
  /*
   * Cảnh báo đi qua khoá DỰ TRỮ, không qua template: thêm cảnh báo cho một trang KHÔNG bắt trang đó
   * sửa `templates.confirm` lẫn khai một nhãn mới. `_`-prefix bị `AI_SLOT_ID_RE` cấm nên nó không
   * bao giờ là một chỗ trống ⇒ `listPlaceholders` dưới đây không thấy nó, không đòi nhãn.
   */
  const warningValue = facts[AI_FACT_WARNING_KEY];
  const warning = typeof warningValue === "string" && warningValue !== "" ? { warning: warningValue } : {};
  const chips: AiTurnChip[] = [
    { kind: "confirm", label: AI_CHIP_CONFIRM_LABEL, payload: { taskId: task.id, version: String(task.version) }, primary: true },
    cancelChip(task.id),
  ];
  if (page.tier === "T1") return { type: "confirm", text, taskId: task.id, taskState: task.state, page: page.id, chips, ...warning };
  const lines: AiTurnCardLine[] = listPlaceholders(template).map((name) => {
    const label = page.labels?.[name];
    if (label === undefined) throw new AiTemplateRenderError("TEMPLATE_LABEL_MISSING", name, page.id);
    // `renderTemplate` đã kiểm chuỗi không rỗng cho mọi chỗ trống của template — lấy lại cùng giá trị.
    return { label, value: values[name] as string };
  });
  return { type: "confirm", text, taskId: task.id, taskState: task.state, page: page.id, card: { title: page.help.title, lines }, chips, ...warning };
}

/** Sau khi chạy xong (T1/T2): `templates.done` + chip dẫn đường `run_page` theo `after` (Người dẫn đường 0061 sẽ lọc theo trạng thái thật). */
/**
 * Lượt BÁO XONG. 🔴 US-AIV2-0034 (review #832 HIGH-1): executor có thể chạy hợp lệ mà **không ghi dòng nào** (cửa ghi REPLAY khoá
 * idempotency). Khi đó nó trả `AI_FACT_NO_WRITE_KEY` và câu phải là `templates.doneAlready` — KHÔNG được rơi về `done`, vì `done`
 * của trang tiền khẳng định những thứ chỉ đúng khi vừa ghi ("Đã mở…", "Chưa ai đóng"). Thiếu `doneAlready` ⇒ NÉM kèm tên khoá:
 * một trang có executor biết no-op mà không có câu cho nó là lỗi của Sổ, phải đỏ ở test chứ không im lặng nói dối về tiền.
 */
export function renderDone(
  page: AiCapabilityPage,
  task: AiTaskView,
  resolved: TemplateValues,
  facts: TemplateValues,
  after: readonly AiCapabilityPage[],
  polished?: PolishedTemplates,
): AiTurn {
  const noWrite = facts[AI_FACT_NO_WRITE_KEY] === "true";
  const key = noWrite ? "doneAlready" : "done";
  const text = renderTemplate(polishedOr(page, key, polished), { ...resolved, ...facts });
  return { type: "answer", text, taskId: task.id, taskState: task.state, page: page.id, chips: runPageChips(after) };
}

/** Trang đọc: `templates.reply` + facts — không phiếu, không chip xác nhận/huỷ. */
export function renderReply(page: AiCapabilityPage, task: AiTaskView, facts: TemplateValues, after: readonly AiCapabilityPage[], polished?: PolishedTemplates): AiTurn {
  const text = renderTemplate(polishedOr(page, "reply", polished), facts);
  return { type: "answer", text, taskId: task.id, taskState: task.state, page: page.id, chips: runPageChips(after) };
}

/**
 * 🔴 US-AIV2-0054 (@UC0154) — TEMPLATE ĐÃ ĐƯỢC LÀM MƯỢT, theo khoá, do caller (`apps/api`) đưa vào.
 *
 * Vì sao là một tham số **tuỳ chọn** của đúng hai hàm này, chứ không phải một tầng render thứ hai:
 * `renderReply`/`renderDone` vẫn là chỗ DUY NHẤT dựng câu của hai cửa đó, và `renderTemplate` vẫn là
 * chỗ DUY NHẤT chèn giá trị. Vắng tham số ⇒ **0 hồi quy, từng chữ** (mọi caller cũ đi qua nguyên vẹn).
 *
 * 🔴 Chỗ trống được chèn **SAU** — kể cả trên bản làm mượt. Đó là toàn bộ điểm của task: `values` vào
 * `renderTemplate` là chuỗi server đã format, không phải thứ LLM viết.
 */
export type PolishedTemplates = Readonly<Partial<Record<keyof AiCapabilityPage["templates"], string>>>;

/**
 * Lấy bản làm mượt nếu có, ngược lại lấy template của Sổ.
 *
 * 🔴 Bản làm mượt rỗng/trắng ⇒ dùng Sổ. Vế này KHÔNG dư: `renderTemplate` trên chuỗi rỗng trả chuỗi
 * rỗng — **không ném** — nên một bản làm mượt rỗng lọt tới đây sẽ thành **một lượt trả lời trắng** sau
 * khi bút toán đã vào sổ. `AiVoicePolishService` đã chặn ca này; đây là hàng rào ở chỗ GHI RA, cho cả
 * caller thứ hai mà hôm nay chưa tồn tại.
 */
function polishedOr(page: AiCapabilityPage, key: keyof AiCapabilityPage["templates"], polished: PolishedTemplates | undefined): string {
  const p = polished?.[key];
  if (typeof p === "string" && p.trim() !== "") return p;
  return templateOf(page, key);
}

/** Tham số route đã thế phải là một đoạn đường dẫn sạch — không `/`, `:`, `?`, `#`, khoảng trắng, không rỗng. */
const SCREEN_PARAM_RE = /^[A-Za-z0-9._~-]+$/;

/** Trang T3/redirect: chỉ nút `open_screen` với route ĐÃ THẾ tham số (`/groups/:groupId/x` + `{groupId}`); còn `:` ⇒ ném. */
export function renderRedirect(page: AiCapabilityPage, params: Readonly<Record<string, string>>): AiTurn {
  const screen = redirectScreenRoute(page, params);
  return {
    type: "redirect",
    text: renderTemplate(templateOf(page, "redirect"), {}),
    page: page.id,
    // nút mở màn là nút chính của lượt redirect — SERVER đặt, client không suy từ kind (#820 L9)
    // US-AIV2-tourguide-0008: `screenLabel` = nhãn nói ĐÚNG màn mở ra khi tên việc (`help.title`) hứa điều màn chưa làm được (vắng ⇒ tên việc).
    chips: [{ kind: "open_screen", label: page.screenLabel ?? page.help.title, payload: { screen }, primary: true }],
  };
}

/**
 * Route ĐÃ THẾ tham số của trang `redirect` — MỘT nguồn cho lượt redirect (`renderRedirect`) và nút thẻ mở thẳng màn (UX-GUIDE-POLISH MED-11,
 * `GuideButtonGate`). Không phải trang redirect / thiếu tham số ⇒ ném (không dựng route hỏng).
 */
export function redirectScreenRoute(page: AiCapabilityPage, params: Readonly<Record<string, string>>): string {
  if (page.tier !== "redirect" || page.screen === undefined) throw new AiTemplateRenderError("TIER_HAS_NO_REDIRECT", page.tier, page.id);
  return fillScreenRoute(page.screen, params, page.id);
}

/** Thế tham số `:name` của route Sổ — MỘT bộ thế cho `screen` (redirect) và `guiScreen` (US-AIV2-0116): thiếu/rác ⇒ ném, không dựng route hỏng. */
function fillScreenRoute(route: string, params: Readonly<Record<string, string>>, pageId: string): string {
  const screen = route.replace(/:([A-Za-z][A-Za-z0-9]*)/g, (_m, name: string) => {
    const v = params[name];
    if (v === undefined || v === "") throw new AiTemplateRenderError("SCREEN_PARAM_MISSING", name, pageId);
    if (!SCREEN_PARAM_RE.test(v)) throw new AiTemplateRenderError("SCREEN_PARAM_INVALID", name);
    return v;
  });
  if (screen.includes(":") || screen.includes("//")) throw new AiTemplateRenderError("SCREEN_PARAM_INVALID", "screen", pageId);
  return screen;
}

/**
 * 🔴 US-AIV2-0116 (@UC0194) — nút MỞ MÀN GUI của một trang GHI (`guiScreen`): cho câu từ chối có lý do dẫn THẲNG tới màn làm được việc đó
 * (nhãn = `help.title` của trang). Trang không khai `guiScreen` ⇒ `null` (caller giữ đường cũ). Thiếu tham số route ⇒ ném như `renderRedirect`.
 */
export function guiScreenChip(page: AiCapabilityPage, params: Readonly<Record<string, string>>): AiTurn["chips"][number] | null {
  if (page.guiScreen === undefined) return null;
  return { kind: "open_screen", label: page.help.title, payload: { screen: fillScreenRoute(page.guiScreen, params, page.id) } };
}

/**
 * Lượt CHỌN giữa vài trang (A/B · «huỷ việc nào» · nút việc LLM đã chọn sau câu khoá-theo-vai · `next` đã lọc `ready()`): chip `run_page` cho
 * ĐÚNG các trang caller đưa vào, ĐÚNG thứ tự đó (caller đã lọc theo vai + cờ), ≤ trần. KHÔNG dùng cho menu khám phá đầy đủ — đó là
 * `renderDiscoveryMenu` (`discovery-menu.ts`, catalog + họ gộp + mục; AI-MENU-A).
 */
export function renderMenu(allowedPages: readonly AiCapabilityPage[], text: string = AI_MENU_TEXT): AiTurn {
  return { type: "menu", text, chips: runPageChips(allowedPages) };
}

/**
 * 🔴 US-AIV2-0052 S01 (@UC0172 @UC0175) — CATALOG câu cho lượt HẠ CẤP. Engine quyết LÝ DO (`decideInterpretation`),
 * chỗ này quyết CÂU. Tầng trình bày không bịa câu, và engine không nhúng câu — hai việc, hai chỗ.
 *
 * 🔴 Vì sao phải là SÁU câu khác nhau chứ không phải một: trước task này cả tám nguyên nhân từ chối của cổng phiên dịch
 * đều ra `AI_MENU_TEXT` = *"Mình chưa rõ ý bạn"*. Với **hết ngân sách** hay **LLM timeout** thì câu đó **sai và đổ lỗi
 * cho người dùng** — trợ lý chưa hề đọc câu của họ. `@UC0175` gọi đúng tên hậu quả: *không có câu "mình chưa hiểu" lần
 * thứ ba*. `render-fallback.test.ts` khoá điều đó bằng một vị-từ quét cả tập đóng: chỉ `not_understood` được dùng
 * `AI_MENU_TEXT`, và sáu câu phải đôi một khác nhau.
 */
export const AI_FALLBACK_TEXT: Readonly<Record<AiFallbackReason, string>> = Object.freeze({
  not_understood: AI_MENU_TEXT,
  low_confidence: "Mình đoán được ý bạn nhưng chưa chắc, nên chưa dám tự làm. Bạn chọn giúp mình nhé.",
  llm_unavailable: "Trợ lý đang bận một chút nên chưa đọc được câu của bạn. Bạn bấm một việc bên dưới, hoặc thử lại sau nhé.",
  too_long: "Câu của bạn dài quá nên mình chưa đọc hết. Bạn nói gọn lại giúp, hoặc bấm một việc bên dưới.",
  /**
   * 🔴 US-AIV2-tourguide-0014 (@UC0377 · N1): 0 cửa chỉ do công tắc HỆ THỐNG (cờ trợ lý theo nhóm — admin hệ thống · cờ rollout · cấu hình trang); trang
   * hướng dẫn mở cho MỌI vai nên vai không bao giờ là lý do. Bản cũ «Bạn hỏi quản trị nhóm để được thêm quyền nhé.» SAI với mọi vai ⇒ chỉ nói sự thật.
   */
  no_door: "Ở đây bạn chưa có việc nào mình làm giúp được.",
  // 🔴 US-AIV2-0111 C07g (@UC0175): lý do này = người dùng bảo thôi mà KHÔNG có việc nào đang mở ⇒ 0 dòng DB đổi. Câu cũ "Đã huỷ…" là
  // lời khai sai (E2E 28/09). Cùng câu Người làm nói ở nhánh "không có gì để huỷ" (`AI_DOER_TEXT.nothingToCancel`).
  cancelled: "Hiện không có việc nào đang làm dở để huỷ. Bạn muốn làm gì tiếp?",
});

/**
 * 🔴 US-AIV2-0114 L01 (@UC0175) — câu cho lượt mà CỔNG LLM CHẶN (`blockReason`), TOÀN PHẦN trên `LLM_BLOCK_REASONS`. Lý do engine vẫn là
 * `llm_unavailable` (một quyết định: không chạy trang, ra menu); chỗ này chỉ chọn CÂU cho đúng nguyên nhân — cùng tư thế "engine quyết lý
 * do, catalog quyết câu" của `AI_FALLBACK_TEXT`.
 *
 * Đo TRƯỚC (develop 6ffc809d1, `linh` 14 câu liền, trần 12 lời gọi LLM/phút/người): lượt 7–8 *"Trợ lý đang bận một chút…"*, lượt 9–14 thẻ
 * STUCK *"Hình như mình chưa hiểu đúng ý bạn"*. Người dùng không biết mình chỉ cần đợi, và câu thứ hai đổ lỗi cho họ về một câu máy chưa đọc.
 *
 * Mỗi câu nói thật ĐIỀU người dùng làm được: `rate_limit` đợi (N giây từ `retryAfterMs` của cổng — SERVER, không qua LLM) · `timeout` gửi
 * lại ngay · `network` thử lại sau ít phút · `budget`/`config` thử lại không giúp gì (hết hạn mức tháng / cần đội Goppy sửa) nên chỉ mời
 * bấm nút. Không câu nào được nói "chưa hiểu".
 */
export const AI_LLM_BLOCKED_TEXT: Readonly<Record<LlmBlockReason, string>> = Object.freeze({
  rate_limit: "Bạn nhắn nhanh quá — đợi mình vài giây rồi gửi lại nhé, hoặc bấm một việc bên dưới.",
  budget: "Trợ lý đã dùng hết hạn mức tháng này nên tạm chưa đọc được câu của bạn. Bạn bấm một việc bên dưới nhé.",
  timeout: "Trợ lý trả lời chậm quá nên lần này chưa đọc kịp câu của bạn. Bạn gửi lại giúp mình, hoặc bấm một việc bên dưới nhé.",
  network: "Mình chưa kết nối được tới trợ lý nên chưa đọc được câu của bạn. Bạn thử lại sau ít phút, hoặc bấm một việc bên dưới nhé.",
  // AI-HARDEN A4: cũng là câu khi production/staging chưa cấu hình model (không bản approved / thiếu key đúng provider) — «chưa sẵn sàng», không «chưa rõ ý».
  config: "Trợ lý chưa sẵn sàng: phía hệ thống còn thiếu cấu hình, cần đội Goppy sửa nên tạm chưa đọc được câu của bạn. Bạn bấm một việc bên dưới nhé.",
});

/** `rate_limit` KHI biết số giây — `{soGiay}` chèn bằng `renderTemplate` (cùng cơ chế chỗ trống của Sổ: chuỗi server, không số thô). */
export const AI_LLM_RATE_LIMIT_WAIT_TEXT = "Bạn nhắn nhanh quá — đợi mình khoảng {soGiay} giây rồi gửi lại nhé, hoặc bấm một việc bên dưới.";

/** `retryAfterMs` ⇒ số giây NGUYÊN để nói: làm tròn LÊN, chặn dưới 1 (không bao giờ "0 giây"). Vắng/rác/âm ⇒ `null` (câu không số). */
export function retryAfterSeconds(retryAfterMs: number | undefined): number | null {
  if (retryAfterMs === undefined || !Number.isFinite(retryAfterMs) || retryAfterMs < 0) return null;
  return Math.max(1, Math.ceil(retryAfterMs / 1000));
}

/** Câu cho một lượt bị cổng LLM chặn. Số giây chỉ có nghĩa với `rate_limit` — lý do khác bỏ qua `retryAfterMs`. */
export function llmBlockedText(reason: LlmBlockReason, retryAfterMs: number | undefined): string {
  const soGiay = reason === "rate_limit" ? retryAfterSeconds(retryAfterMs) : null;
  return soGiay === null ? AI_LLM_BLOCKED_TEXT[reason] : renderTemplate(AI_LLM_RATE_LIMIT_WAIT_TEXT, { soGiay: String(soGiay) });
}

/**
 * 🔴 AI-PAY-REPORT-WORDING (lô review AI 15/10, L2#7 · @UC0175 @UC0197) — câu đặt TRÊN phiếu ĐANG CHỜ Đồng ý khi câu người dùng vừa gõ (thường là
 * câu SỬA: "à không, 300k") KHÔNG tới được LLM (cổng chặn — `blockedReason` — hoặc LLM ném). Trước bản vá: phiếu cũ vẽ lại không một câu ⇒ người
 * dùng tưởng đã sửa rồi bấm Đồng ý cho số CŨ (cam kết 5: phiếu không đổi — nên phải NÓI là không đổi).
 *
 * CÙNG tập khoá (`LlmBlockReason`) và CÙNG phép tính số giây (`retryAfterSeconds`) với câu menu `AI_LLM_BLOCKED_TEXT` — một nguồn cho "vì sao
 * chưa đọc được". KHÁC câu chữ có chủ đích: câu menu mời "bấm một việc bên dưới", mà dưới một phiếu tiền thì nút bên dưới là ĐỒNG Ý.
 * Mỗi câu: nguyên nhân · phiếu VẪN NHƯ CŨ · đừng bấm Đồng ý nếu chưa đúng · điều làm được (gửi lại / đợi / Huỷ rồi làm lại trên màn hình).
 */
const UNREAD_OPEN_TASK_UNCHANGED = `phiếu dưới đây vẫn như cũ, chưa sửa gì. Phiếu chưa đúng thì đừng bấm ${AI_CHIP_CONFIRM_LABEL}`;
export const AI_LLM_UNREAD_OPEN_TASK_TEXT: Readonly<Record<LlmBlockReason, string>> = Object.freeze({
  rate_limit: `Bạn nhắn nhanh quá nên mình chưa đọc câu bạn vừa gõ — ${UNREAD_OPEN_TASK_UNCHANGED}; đợi vài giây rồi gửi lại câu sửa nhé.`,
  budget: `Trợ lý đã dùng hết hạn mức tháng này nên chưa đọc được câu bạn vừa gõ — ${UNREAD_OPEN_TASK_UNCHANGED}; muốn sửa thì bấm ${AI_CHIP_CANCEL_LABEL} rồi làm lại trên màn hình nhé.`,
  timeout: `Trợ lý trả lời chậm quá nên chưa đọc kịp câu bạn vừa gõ — ${UNREAD_OPEN_TASK_UNCHANGED}; bạn gửi lại câu sửa giúp mình nhé.`,
  network: `Mình chưa kết nối được tới trợ lý nên chưa đọc được câu bạn vừa gõ — ${UNREAD_OPEN_TASK_UNCHANGED}; bạn gửi lại câu sửa sau ít phút nhé.`,
  config: `Trợ lý chưa sẵn sàng (phía hệ thống còn thiếu cấu hình) nên chưa đọc được câu bạn vừa gõ — ${UNREAD_OPEN_TASK_UNCHANGED}; muốn sửa thì bấm ${AI_CHIP_CANCEL_LABEL} rồi làm lại trên màn hình nhé.`,
});
/** LLM ném mà cổng không trả lý do chặn (`rejected = llm_error`) — cùng nghĩa câu menu `llm_unavailable` ("đang bận"). */
const AI_LLM_UNREAD_OPEN_TASK_BUSY = `Trợ lý đang bận một chút nên chưa đọc được câu bạn vừa gõ — ${UNREAD_OPEN_TASK_UNCHANGED}; bạn gửi lại câu sửa sau ít giây nhé.`;
/** `rate_limit` KHI biết số giây (SERVER) — `{soGiay}` chèn bằng `renderTemplate`, như `AI_LLM_RATE_LIMIT_WAIT_TEXT`. */
const AI_LLM_UNREAD_OPEN_TASK_WAIT = `Bạn nhắn nhanh quá nên mình chưa đọc câu bạn vừa gõ — ${UNREAD_OPEN_TASK_UNCHANGED}; đợi khoảng {soGiay} giây rồi gửi lại câu sửa nhé.`;

/** Câu cho phiếu đang chờ Đồng ý mà câu vừa gõ chưa tới LLM. `blocked` vắng ⇒ LLM ném (`llm_error`). */
export function llmUnreadOpenTaskText(blocked: AiLlmBlockedDetail | undefined): string {
  if (blocked === undefined) return AI_LLM_UNREAD_OPEN_TASK_BUSY;
  const soGiay = blocked.reason === "rate_limit" ? retryAfterSeconds(blocked.retryAfterMs) : null;
  return soGiay === null ? AI_LLM_UNREAD_OPEN_TASK_TEXT[blocked.reason] : renderTemplate(AI_LLM_UNREAD_OPEN_TASK_WAIT, { soGiay: String(soGiay) });
}

/**
 * 🔴 UX-OPEN-TASK-EDIT (lô GUIDE-UX 15/10, U-2 · nghiệm thu UX-H2 + MED-12 · @UC0165) — vế CÒN LẠI của câu trên: LLM ĐÃ đọc câu vừa gõ khi một
 * phiếu đang mở, nhưng câu đó KHÔNG áp được vào phiếu (Người phiên dịch chọn một việc GHI khác không cùng ô, hoặc không chọn được việc nào).
 * Đo (LLM thật 15/10, `tam`): phiếu «Mở đợt thu đều» chờ Đồng ý, gõ "đặt tên là Quỹ tháng 11, hạn 30/11 nhé" ⇒ phiếu CŨ vẽ lại không một câu.
 * Luật: KHÔNG BAO GIỜ vẽ lại phiếu cũ im lặng khi người dùng vừa nói điều mới — câu nói rõ phiếu chưa đổi + lối ra.
 *
 * TÁI DÙNG nửa câu "phiếu dưới đây vẫn như cũ, chưa sửa gì. Phiếu chưa đúng thì đừng bấm Đồng ý" của câu LLM-không-đọc-được (#1427) — một nguồn
 * cho lời khai "phiếu không đổi". `{viec}`/`{viecMoi}` = `help.title` của trang (chuỗi Sổ, không phải chữ LLM), chèn bằng `renderTemplate`.
 *  · `AI_OPEN_TASK_OTHER_WORK_TEXT` — phiếu CHỜ ĐỒNG Ý + câu là một việc khác làm được ⇒ nút đổi việc đứng dưới.
 *  · `AI_OPEN_TASK_OTHER_WORK_ASKING_TEXT` — đang HỎI DỞ một ô (MED-12: trước chỉ có "xong việc này trước, hoặc bấm Huỷ" ⇒ phải huỷ tay rồi gõ lại).
 *  · `AI_OPEN_TASK_UNCHANGED_TEXT` — phiếu chờ Đồng ý mà KHÔNG có việc nào để đổi sang (LLM không chọn được trang · việc khoá với người này).
 */
export const AI_CHIP_SWITCH_TASK_LABEL = "Huỷ phiếu này, làm việc mới";
export const AI_CHIP_KEEP_TASK_LABEL = "Giữ phiếu";
export const AI_OPEN_TASK_OTHER_WORK_TEXT = `Phiếu “{viec}” đang chờ bạn xác nhận — mình CHƯA đổi gì trên phiếu. Câu bạn vừa gõ là một việc khác: “{viecMoi}”. Muốn làm việc đó thì bấm “${AI_CHIP_SWITCH_TASK_LABEL}”; ${UNREAD_OPEN_TASK_UNCHANGED}.`;
export const AI_OPEN_TASK_OTHER_WORK_ASKING_TEXT = `Mình đang làm dở việc “{viec}” với bạn nên chưa làm việc “{viecMoi}”. Muốn làm việc mới thì bấm “${AI_CHIP_SWITCH_TASK_LABEL}”; còn không thì trả lời câu hỏi bên dưới để làm tiếp.`;
export const AI_OPEN_TASK_UNCHANGED_TEXT = `Phiếu “{viec}” đang chờ bạn xác nhận — mình chưa áp được câu bạn vừa gõ vào phiếu, nên ${UNREAD_OPEN_TASK_UNCHANGED}; bạn nói rõ chỗ cần sửa, hoặc bấm ${AI_CHIP_CANCEL_LABEL}.`;

/** Câu đặt trên phiếu đang mở khi câu vừa gõ là một việc khác (`viecMoi` = tên việc đó) — `asking` = phiếu đang hỏi dở một ô, không chờ Đồng ý. */
export function openTaskOtherWorkText(viec: string, viecMoi: string, asking: boolean): string {
  return renderTemplate(asking ? AI_OPEN_TASK_OTHER_WORK_ASKING_TEXT : AI_OPEN_TASK_OTHER_WORK_TEXT, { viec, viecMoi });
}

/** Câu ĐẦU của lượt sau khi bấm «Huỷ phiếu này, làm việc mới» — lời khai về một thay đổi DB THẬT (phiếu cũ vừa `cancelled`), rồi tới bước của việc mới. */
export const AI_OPEN_TASK_SWITCHED_TEXT = "Đã huỷ phiếu “{viec}”.";
export function openTaskSwitchedText(viec: string): string {
  return renderTemplate(AI_OPEN_TASK_SWITCHED_TEXT, { viec });
}
/** Phiếu cũ ĐÃ huỷ nhưng việc mới bị từ chối khi mở (cổng đổi giữa lúc kiểm và lúc mở) — nói thật cả hai vế, rồi menu. */
export const AI_OPEN_TASK_SWITCH_FAILED_TEXT = "Đã huỷ phiếu “{viec}”, nhưng mình chưa mở được việc mới. Bạn gõ lại yêu cầu, hoặc chọn một việc bên dưới nhé.";
export function openTaskSwitchFailedText(viec: string): string {
  return renderTemplate(AI_OPEN_TASK_SWITCH_FAILED_TEXT, { viec });
}

/**
 * 🔴 review #1435 M-2 (@UC0165 · AI-INV-4) — phiếu CHỜ Đồng ý mà người dùng gõ câu khẳng định thuần ("ok", "đồng ý"): chữ KHÔNG BAO GIỜ là xác
 * nhận — mời bấm nút, nói rõ vì sao chưa ghi.
 */
export const AI_OPEN_TASK_ACK_TEXT = `Phiếu đúng rồi thì bấm “${AI_CHIP_CONFIRM_LABEL}” bên dưới để ghi nhé — mình không ghi khi chỉ nhận chữ.`;

/**
 * 🔴 review #1435 M-1 (@UC0165) — lời sửa (a) áp từ trang anh em KHÔNG có một ô của phiếu (vd hạn chót) ⇒ ô đó giữ nguyên, và thẻ mới phải NÓI ra
 * (không để người dùng đoán "hạn có đổi theo không"). `{giuNguyen}` = "<nhãn Sổ> vẫn giữ: <giá trị trên thẻ>", nối "; ".
 */
export const AI_OPEN_TASK_EDIT_KEPT_TEXT = "Mình đã sửa phiếu theo câu bạn vừa gõ; {giuNguyen}.";
export function openTaskEditKeptText(kept: readonly { readonly label: string; readonly value: string }[]): string {
  return renderTemplate(AI_OPEN_TASK_EDIT_KEPT_TEXT, { giuNguyen: kept.map((k) => `${k.label} vẫn giữ: ${k.value}`).join("; ") });
}

/** Câu đặt trên phiếu đang chờ Đồng ý khi câu vừa gõ không áp được và không có việc nào để đổi sang. */
export function openTaskUnchangedText(viec: string): string {
  return renderTemplate(AI_OPEN_TASK_UNCHANGED_TEXT, { viec });
}

/** Chi tiết chặn của cổng LLM (`AiInterpretResult.blockedReason` + `retryAfterMs`) — chỉ đổi CÂU của lý do `llm_unavailable`. */
export interface AiLlmBlockedDetail {
  readonly reason: LlmBlockReason;
  readonly retryAfterMs?: number;
}

/** Nút `run_page` của từng trang theo ĐÚNG thứ tự đưa vào (lượt chọn vài trang) — ≤ trần menu. */
function runPageChips(pages: readonly AiCapabilityPage[]): AiTurnChip[] {
  return pages.slice(0, AI_MENU_MAX_CHIPS).map((p) => ({ kind: "run_page", label: p.help.title, payload: { page: p.id } }));
}
